import React, { useState } from 'react';
import { Search, Plus, UtensilsCrossed } from 'lucide-react';

import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { getImageUrl } from '../../contexts/AuthContext';
import { formatAmount } from '../../utils/format';

import PullToRefresh from './PullToRefresh';
const CustomerHomeView = ({ products, categories, tenant, onAddToCart, onRefresh, isRefreshing }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || p.category_id === selectedCategory;
    return matchesSearch && matchesCategory && p.is_available !== false;
  });

  return (
    <PullToRefresh onRefresh={onRefresh} isRefreshing={isRefreshing}>
    <div className="pb-4">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 -mx-4 -mt-4 px-4 pt-12 pb-6 mb-4 rounded-b-3xl shadow-lg">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-white/20 backdrop-blur-xl rounded-2xl flex items-center justify-center">
            <UtensilsCrossed className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">{tenant?.name || 'SmartDine'}</h1>
            <p className="text-sm text-white/80">Menú Digital</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar productos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-11 bg-white/90 backdrop-blur-xl border-0 text-gray-800 placeholder:text-gray-400 rounded-xl shadow-sm"
          />
        </div>
      </div>

      {/* Categories */}
      {categories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide -mx-1 px-1">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              !selectedCategory
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Todos
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === cat.id
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat.icon && <span className="mr-1">{cat.icon}</span>}
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-2 gap-3">
        {filteredProducts.map((product) => {
          const hasVariants = product.variants && product.variants.length > 0;
          const minPrice = hasVariants
            ? Math.min(...product.variants.filter(v => v.is_available !== false).map(v => v.price))
            : product.price;

          return (
            <Card
              key={product.id}
              className="overflow-hidden bg-white border border-gray-100 shadow-sm active:scale-[0.98] transition-transform"
            >
              {/* Image */}
              <div className="h-28 bg-gradient-to-br from-orange-100 to-red-100 overflow-hidden">
                {product.image_url ? (
                  <img
                    src={getImageUrl(product.image_url)}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <UtensilsCrossed className="w-8 h-8 text-orange-300" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-3">
                <h3 className="font-semibold text-gray-800 text-sm leading-tight mb-1 line-clamp-2">
                  {product.name}
                </h3>
                {product.description && (
                  <p className="text-xs text-gray-500 mb-2 line-clamp-1">{product.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold text-orange-600">
                    ${formatAmount(minPrice)}
                    {hasVariants && <span className="text-xs text-gray-400 font-normal">+</span>}
                  </span>
                  <button
                    onClick={() => onAddToCart(product)}
                    className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center shadow-sm active:scale-90 transition-transform"
                  >
                    <Plus className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <UtensilsCrossed className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No hay productos disponibles</p>
        </div>
      )}
    </div>
    </PullToRefresh>
  );
};

export default CustomerHomeView;
